import React, { useState, useCallback, useMemo, useEffect, useContext, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faClock,
  faStar,
  faDotCircle,
  faChartBar,
  faSortAmountDown,
  faFileAlt,
} from "@fortawesome/free-solid-svg-icons";
import { T } from "react-translator-component";
import { i18nValue } from "../functions";
import { getCDNImage } from "../globals";
import ThanksReviews from "./ThanksReviews";
import RankingQuestionInput from "./RankingQuestionInput";
import TextareaQuestionInput from "./TextareaQuestionInput";
import { useReviews } from "../hooks/useReviews";
import { CheckInContext } from "../context/checkin";
import {
  formatReviewQuestionTypeTitle,
  getSimpleReviewConfig,
  getReviewFlowNodeAutoAdvanceDefaults,
  normalizeReviewFlow,
  parseReviewAnswers,
  resolveReviewFlowNodeTheme,
  resolveReviewFlowNextNodeId,
  resolveReviewFlowStartNodeId,
  resolveFirstMainPrincipalEntryNodeId,
  REVIEW_QUESTION_TYPES,
  REVIEW_FLOW_FONT_FAMILIES,
  REVIEW_FLOW_LAYOUT_PRESETS,
  REVIEW_FLOW_MOBILE_CONTENT_POSITIONS,
  REVIEW_SETTING_TYPES,
  SIMPLE_REVIEW_DESIGN_DEFAULTS,
  SIMPLE_REVIEW_FONT_FAMILIES,
} from "./Reviews/reviewQuestionBlocks";

const EMPTY_PREVIEW_SIMULATED_UPSELLING_IDS = [];

/** Alineado con el preview del backoffice (ReviewQuestionPreviewPanel). */
const typeVisuals = {
  [REVIEW_QUESTION_TYPES.RATING_QUESTION]: { icon: faStar, accent: "#f59e0b" },
  [REVIEW_QUESTION_TYPES.SINGLE_CHOICE_QUESTION]: { icon: faDotCircle, accent: "#3b82f6" },
  [REVIEW_QUESTION_TYPES.OPINION_SCALE_QUESTION]: { icon: faChartBar, accent: "#10b981" },
  [REVIEW_QUESTION_TYPES.RANKING_QUESTION]: { icon: faSortAmountDown, accent: "#8b5cf6" },
  [REVIEW_QUESTION_TYPES.TEXTAREA_QUESTION]: { icon: faFileAlt, accent: "#ec4899" },
  [REVIEW_QUESTION_TYPES.LANDING_PAGE]: { icon: faClock, accent: "#0f766e" },
};

const getReviewFlowImage = (value = "") => {
  if (!value) {
    return "";
  }
  if (/^https?:\/\//i.test(value) || value.startsWith("data:")) {
    return value;
  }
  return getCDNImage(value);
};

const withHexOpacity = (color, opacity) =>
  /^#(?:[0-9A-F]{6})$/i.test(String(color || "").trim()) ? `${color}${opacity}` : color;

const resolveReviewFlowFontFamily = (node) => {
  const fontFamilyKey = `${node?.fontFamily || ""}`.trim();
  return fontFamilyKey && REVIEW_FLOW_FONT_FAMILIES[fontFamilyKey] ? REVIEW_FLOW_FONT_FAMILIES[fontFamilyKey] : undefined;
};

const resolveReviewFlowFontSize = (value) => {
  const nextValue = Number(value);
  return Number.isFinite(nextValue) && nextValue > 0 ? nextValue : null;
};

const resolveReviewFlowBlockHeight = (value) => {
  const nextValue = Number(value);
  return Number.isFinite(nextValue) && nextValue > 0 ? nextValue : null;
};

const resolveReviewFlowContentPositionJustifyContent = (value) => {
  if (value === REVIEW_FLOW_MOBILE_CONTENT_POSITIONS.TOP) {
    return "flex-start";
  }
  if (value === REVIEW_FLOW_MOBILE_CONTENT_POSITIONS.BOTTOM) {
    return "flex-end";
  }
  return "center";
};

const resolveReviewFlowNodeAutoAdvanceConfig = (node) => {
  const defaults = getReviewFlowNodeAutoAdvanceDefaults(node);
  const explicitDelayMs = Number(node?.autoAdvanceDelayMs);

  return {
    enabled: typeof node?.autoAdvanceEnabled === "boolean" ? node.autoAdvanceEnabled : defaults.enabled,
    delayMs: Number.isFinite(explicitDelayMs) && explicitDelayMs >= 0 ? explicitDelayMs : defaults.delayMs,
  };
};

const resolveReviewFlowLayoutPreset = (value) => {
  return Object.values(REVIEW_FLOW_LAYOUT_PRESETS).includes(value) ? value : REVIEW_FLOW_LAYOUT_PRESETS.AUTO;
};

const getAutomaticReviewFlowLayoutPreset = (node, { hasImage = false } = {}) => {
  if (node?.type === REVIEW_QUESTION_TYPES.TEXTAREA_QUESTION) {
    return REVIEW_FLOW_LAYOUT_PRESETS.QUESTION_FOCUS;
  }
  if (hasImage) {
    return REVIEW_FLOW_LAYOUT_PRESETS.BALANCED;
  }
  return REVIEW_FLOW_LAYOUT_PRESETS.QUICK_ANSWER;
};

const getReviewFlowLayoutConfig = (node, { hasImage = false, compact = false } = {}) => {
  const rawPreset = resolveReviewFlowLayoutPreset(node?.layoutPreset);
  const effectivePreset =
    rawPreset === REVIEW_FLOW_LAYOUT_PRESETS.AUTO
      ? getAutomaticReviewFlowLayoutPreset(node, { hasImage })
      : rawPreset === REVIEW_FLOW_LAYOUT_PRESETS.IMAGE_FOCUS && !hasImage
        ? REVIEW_FLOW_LAYOUT_PRESETS.BALANCED
        : rawPreset;

  switch (effectivePreset) {
    case REVIEW_FLOW_LAYOUT_PRESETS.QUESTION_FOCUS:
      return {
        effectivePreset,
        contentJustifyContent: "flex-start",
        contentGap: compact ? 24 : 30,
        answerMarginTop: compact ? 24 : 28,
        footerMarginTop: compact ? 24 : 22,
        cardAnswerMarginTop: 18,
        groupedAnswerMarginTop: 16,
        cardContainerJustifyContent: compact ? "center" : "flex-start",
        mobileImageHeight: 170,
      };
    case REVIEW_FLOW_LAYOUT_PRESETS.BALANCED:
      return {
        effectivePreset,
        contentJustifyContent: "space-between",
        contentGap: compact ? 24 : 28,
        answerMarginTop: "auto",
        footerMarginTop: compact ? 28 : 22,
        cardAnswerMarginTop: 12,
        groupedAnswerMarginTop: 12,
        cardContainerJustifyContent: "center",
        mobileImageHeight: 220,
      };
    case REVIEW_FLOW_LAYOUT_PRESETS.IMAGE_FOCUS:
      return {
        effectivePreset,
        contentJustifyContent: "flex-start",
        contentGap: compact ? 16 : 18,
        answerMarginTop: compact ? 14 : 16,
        footerMarginTop: compact ? 18 : 20,
        cardAnswerMarginTop: 10,
        groupedAnswerMarginTop: 10,
        cardContainerJustifyContent: compact ? "center" : "flex-start",
        mobileImageHeight: 300,
      };
    case REVIEW_FLOW_LAYOUT_PRESETS.QUICK_ANSWER:
    default:
      return {
        effectivePreset,
        contentJustifyContent: "flex-start",
        contentGap: compact ? 18 : 20,
        answerMarginTop: compact ? 16 : 18,
        footerMarginTop: compact ? 20 : 22,
        cardAnswerMarginTop: 10,
        groupedAnswerMarginTop: 10,
        cardContainerJustifyContent: "flex-start",
        mobileImageHeight: 150,
      };
  }
};

const getReadableTextColor = (color, light = "#FFFFFF", dark = "#111111") => {
  const normalized = String(color || "").trim();
  if (!/^#(?:[0-9A-F]{6})$/i.test(normalized)) {
    return dark;
  }
  const value = normalized.slice(1);
  const red = parseInt(value.slice(0, 2), 16);
  const green = parseInt(value.slice(2, 4), 16);
  const blue = parseInt(value.slice(4, 6), 16);
  const brightness = (red * 299 + green * 587 + blue * 114) / 1000;
  return brightness >= 160 ? dark : light;
};

const readStoredReviewData = (settingId) => {
  try {
    return JSON.parse(localStorage.getItem(`anonymousVoteData${settingId}`)) || {};
  } catch {
    return {};
  }
};

const hasValue = (v) => {
  if (v === undefined || v === null || v === "") return false;
  if (typeof v === "number" && Number.isNaN(v)) return false;
  return true;
};

/**
 * Formulario de flujo de review (pantalla completa tipo Typeform o embebido en modal).
 */
const ReviewFlowForm = ({
  ratingId,
  reviewFlow,
  reviewSettingType = REVIEW_SETTING_TYPES.FLOW,
  language,
  onFinish,
  settingId,
  roomOccupationId,
  layout = "fullscreen",
  onDismiss,
  previewMode = false,
  previewSelectedNodeId = null,
  previewPlayMode = false,
  previewSimulatedUpsellingIds = EMPTY_PREVIEW_SIMULATED_UPSELLING_IDS,
  simpleReviewConfig = null,
}) => {
  const { data: checkInData } = useContext(CheckInContext);
  const storedData = useMemo(
    () => (previewMode ? {} : readStoredReviewData(settingId)),
    [previewMode, settingId]
  );
  const [answers, setAnswers] = useState(() => parseReviewAnswers(storedData?.extraReviews));
  const [comment, setComment] = useState(storedData?.comment || "");
  const [contactInfo, setContactInfo] = useState(storedData?.contactInfo || {});
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  const [footerHint, setFooterHint] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submittingFlowAnswers, setSubmittingFlowAnswers] = useState(false);
  const [flowExtraReviewsSent, setFlowExtraReviewsSent] = useState(false);
  const [currentPath, setCurrentPath] = useState([]);
  const [currentNodeId, setCurrentNodeId] = useState(null);
  const [textareaLocalValue, setTextareaLocalValue] = useState("");
  const [rankingHasAnswer, setRankingHasAnswer] = useState(false);
  const rankingQuestionRef = useRef(null);
  const textareaQuestionRef = useRef(null);
  const autoAdvanceTimeoutRef = useRef(null);
  const lastTextareaAutoAdvanceKeyRef = useRef("");
  const normalizedFlow = useMemo(() => normalizeReviewFlow(reviewFlow), [reviewFlow]);
  const reviewRuntimeContext = useMemo(() => {
    const fromCheckIn = Array.isArray(checkInData?.crossUpSelling) ? checkInData.crossUpSelling : [];
    if (
      previewMode &&
      Array.isArray(previewSimulatedUpsellingIds) &&
      previewSimulatedUpsellingIds.length > 0
    ) {
      const simulated = previewSimulatedUpsellingIds
        .map((id) => ({ id: `${id || ""}`.trim() }))
        .filter((item) => item.id);
      return { crossUpSelling: simulated };
    }
    return { crossUpSelling: fromCheckIn };
  }, [checkInData?.crossUpSelling, previewMode, previewSimulatedUpsellingIds]);
  const { setDoneRating, update } = useReviews();
  const isPreviewInspectMode = previewMode && !previewPlayMode;
  const isSimpleGroupedMode =
    reviewSettingType === REVIEW_SETTING_TYPES.SIMPLE || reviewSettingType === "review";

  const enabledNodes = useMemo(
    () => (normalizedFlow.nodes || []).filter((n) => n.enabled !== false),
    [normalizedFlow]
  );
  const simpleQuestions = useMemo(
    () => enabledNodes.filter((node) => node.type !== REVIEW_QUESTION_TYPES.LANDING_PAGE),
    [enabledNodes]
  );
  const totalQuestionCount = Math.max(enabledNodes.length, 1);
  const totalWizardSteps = totalQuestionCount + 1;

  useEffect(() => {
    const stored = previewMode ? {} : readStoredReviewData(settingId);
    const initialAnswers = previewMode ? {} : parseReviewAnswers(stored?.extraReviews);
    const inspectNodeId =
      isPreviewInspectMode && normalizedFlow.nodeById?.[previewSelectedNodeId]
        ? previewSelectedNodeId
        : null;
    if (isSimpleGroupedMode) {
      setAnswers(initialAnswers);
      setComment(previewMode ? "" : stored?.comment || "");
      setContactInfo(previewMode ? {} : stored?.contactInfo || {});
      setErrors({});
      setCurrentNodeId(null);
      setCurrentPath([]);
      setFlowExtraReviewsSent(false);
      setFooterHint(null);
    } else {
      const startNodeId =
        inspectNodeId ||
        (reviewSettingType === REVIEW_SETTING_TYPES.FLOW
          ? resolveFirstMainPrincipalEntryNodeId(normalizedFlow, initialAnswers, reviewRuntimeContext)
          : resolveReviewFlowStartNodeId(normalizedFlow, initialAnswers, reviewRuntimeContext));
      setAnswers(initialAnswers);
      setComment(previewMode ? "" : stored?.comment || "");
      setContactInfo(previewMode ? {} : stored?.contactInfo || {});
      setErrors({});
      setFocusedField(null);
      setCurrentNodeId(startNodeId);
      setCurrentPath(startNodeId ? [startNodeId] : []);
      setFlowExtraReviewsSent(false);
      setFooterHint(null);
    }
  }, [
    normalizedFlow,
    reviewSettingType,
    settingId,
    previewMode,
    previewSelectedNodeId,
    isPreviewInspectMode,
    isSimpleGroupedMode,
    reviewRuntimeContext,
    previewSimulatedUpsellingIds,
  ]);

  // Reset textarea local value when node changes
  useEffect(() => {
    const activeNode = currentNodeId ? normalizedFlow.nodeById?.[currentNodeId] : null;
    lastTextareaAutoAdvanceKeyRef.current = "";
    if (activeNode?.type === REVIEW_QUESTION_TYPES.TEXTAREA_QUESTION) {
      setTextareaLocalValue(answers?.[activeNode.id] || "");
    } else {
      setTextareaLocalValue("");
    }
  }, [currentNodeId, normalizedFlow, answers]);

  // Reset ranking answer state when node changes
  useEffect(() => {
    const activeNode = currentNodeId ? normalizedFlow.nodeById?.[currentNodeId] : null;
    if (activeNode?.type === REVIEW_QUESTION_TYPES.RANKING_QUESTION) {
      setRankingHasAnswer(!!answers?.[activeNode.id]);
    } else {
      setRankingHasAnswer(false);
    }
  }, [currentNodeId, normalizedFlow, answers]);

  const clearAutoAdvanceTimeout = useCallback(() => {
    if (autoAdvanceTimeoutRef.current) {
      window.clearTimeout(autoAdvanceTimeoutRef.current);
      autoAdvanceTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => () => clearAutoAdvanceTimeout(), [clearAutoAdvanceTimeout]);

  const persistLocalData = (extraData = {}) => {
    if (previewMode) {
      return;
    }
    const existingReviewData = readStoredReviewData(settingId);
    localStorage.setItem(
      `anonymousVoteData${settingId}`,
      JSON.stringify({
        ...existingReviewData,
        ReviewSetting: { questions: JSON.stringify(reviewFlow || {}) },
        ...extraData,
      })
    );
  };

  const finishFlowSection = (updatedAnswers, pathUpToCurrent) => {
    if (previewMode) {
      setFlowExtraReviewsSent(true);
      setCurrentNodeId(null);
      setCurrentPath(pathUpToCurrent);
      return;
    }
    setSubmittingFlowAnswers(true);
    update(ratingId, { extraReviews: updatedAnswers })
      .then(() => {
        setFlowExtraReviewsSent(true);
        setCurrentNodeId(null);
        setCurrentPath(pathUpToCurrent);
      })
      .finally(() => {
        setSubmittingFlowAnswers(false);
      });
  };

  const continueAfterAnswer = (nodeId, updatedAnswers, pathUpToCurrent, { delayMs = 0 } = {}) => {
    const nextNodeId = resolveReviewFlowNextNodeId(normalizedFlow, nodeId, updatedAnswers, {
      context: reviewRuntimeContext,
      reviewSettingType,
    });

    const commitAdvance = () => {
      autoAdvanceTimeoutRef.current = null;
      if (nextNodeId) {
        setCurrentNodeId(nextNodeId);
        setCurrentPath([...pathUpToCurrent, nextNodeId]);
      } else if (reviewSettingType === REVIEW_SETTING_TYPES.FLOW) {
        finishFlowSection(updatedAnswers, pathUpToCurrent);
      } else {
        setCurrentNodeId(null);
        setCurrentPath(pathUpToCurrent);
      }
    };

    if (!(Number(delayMs) > 0)) {
      clearAutoAdvanceTimeout();
      commitAdvance();
      return;
    }

    clearAutoAdvanceTimeout();
    autoAdvanceTimeoutRef.current = window.setTimeout(commitAdvance, Number(delayMs));
  };

  const handleAnswerChange = (nodeId, value, { autoAdvanceOverride, delayMsOverride } = {}) => {
    setFooterHint(null);
    if (isSimpleGroupedMode) {
      const updatedAnswers = {
        ...(answers || {}),
        [nodeId]: value,
      };
      setAnswers(updatedAnswers);
      persistLocalData({ extraReviews: updatedAnswers });
      if (!previewMode) {
        update(ratingId, { extraReviews: updatedAnswers });
      }
      return { updatedAnswers, pathUpToCurrent: [] };
    }
    const currentIndex = currentPath.indexOf(nodeId);
    const pathUpToCurrent =
      currentIndex >= 0 ? currentPath.slice(0, currentIndex + 1) : [...currentPath, nodeId];
    const updatedAnswers = { ...(answers || {}) };
    Object.keys(updatedAnswers).forEach((answerKey) => {
      if (!pathUpToCurrent.includes(answerKey) && answerKey !== nodeId) {
        delete updatedAnswers[answerKey];
      }
    });
    updatedAnswers[nodeId] = value;

    setAnswers(updatedAnswers);

    if (isPreviewInspectMode) {
      return { updatedAnswers, pathUpToCurrent };
    }

    if (!previewMode && reviewSettingType !== REVIEW_SETTING_TYPES.FLOW) {
      update(ratingId, { extraReviews: updatedAnswers });
    }

    persistLocalData({ extraReviews: updatedAnswers });
    const autoAdvanceConfig = resolveReviewFlowNodeAutoAdvanceConfig(normalizedFlow.nodeById?.[nodeId]);
    const shouldAutoAdvance =
      typeof autoAdvanceOverride === "boolean" ? autoAdvanceOverride : autoAdvanceConfig.enabled;
    const delayMs = Number.isFinite(Number(delayMsOverride)) ? Number(delayMsOverride) : autoAdvanceConfig.delayMs;

    if (shouldAutoAdvance) {
      continueAfterAnswer(nodeId, updatedAnswers, pathUpToCurrent, { delayMs });
    }

    return { updatedAnswers, pathUpToCurrent };
  };

  const advanceWithoutAnswer = () => {
    const currentNode = currentNodeId ? normalizedFlow.nodeById?.[currentNodeId] : null;
    if (!currentNode) return;
    if (isPreviewInspectMode) return;
    setFooterHint(null);
    const pathUpToCurrent =
      currentPath.indexOf(currentNode.id) >= 0
        ? currentPath.slice(0, currentPath.indexOf(currentNode.id) + 1)
        : [...currentPath, currentNode.id];

    const nextNodeId = resolveReviewFlowNextNodeId(normalizedFlow, currentNode.id, answers, {
      context: reviewRuntimeContext,
      reviewSettingType,
    });

    if (nextNodeId) {
      setCurrentNodeId(nextNodeId);
      setCurrentPath([...pathUpToCurrent, nextNodeId]);
    } else if (reviewSettingType === REVIEW_SETTING_TYPES.FLOW) {
      finishFlowSection(answers, pathUpToCurrent);
    } else {
      setCurrentNodeId(null);
      setCurrentPath(pathUpToCurrent);
    }
  };

  const validateContactInfo = () => {
    const newErrors = {};
    if (!contactInfo.name || contactInfo.name.length < 3) {
      newErrors.name = T("review.validation_name_min");
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!contactInfo.email || !emailRegex.test(contactInfo.email)) {
      newErrors.email = T("review.validation_email");
    }
    const phoneRegex = /^\d{6,}$/;
    if (!contactInfo.phone || !phoneRegex.test(contactInfo.phone)) {
      newErrors.phone = T("review.validation_phone");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    setLoading(true);
    setFooterHint(null);

    const payload = {};
    if (reviewSettingType !== REVIEW_SETTING_TYPES.FLOW || !flowExtraReviewsSent) {
      payload.extraReviews = answers;
    }
    if (comment) {
      payload.comment = comment;
    }

    if (contactInfo && Object.keys(contactInfo).length !== 0) {
      if (!validateContactInfo()) {
        setLoading(false);
        return;
      }
      payload.contactInfo = contactInfo;
    }

    if (previewMode) {
      const firstNumericReview =
        (normalizedFlow.nodes || []).reduce((acc, node) => {
          if (typeof acc === "number") {
            return acc;
          }
          const value = answers?.[node.id];
          return typeof value === "number" ? value : acc;
        }, null) || 0;

      setTimeout(() => {
        setLoading(false);
        if (onFinish) {
          onFinish({
            reviewSubmitted: true,
            review: firstNumericReview,
            extraReviews: answers,
            comment: comment || null,
            contactInfo: contactInfo || null,
            ReviewSetting: { questions: JSON.stringify(reviewFlow || {}) },
          });
        }
      }, 600);
      return;
    }

    update(ratingId, payload);

    const existingStore = readStoredReviewData(settingId);
    persistLocalData({
      extraReviews: answers,
      comment: comment || null,
      contactInfo: contactInfo || null,
      reviewSubmitted: true,
      review: typeof existingStore?.review === "number" ? existingStore.review : 0,
    });

    setTimeout(() => {
      setLoading(false);
      setDoneRating(true);
      onFinish();
    }, 1400);
  };

  const handleBackStep = () => {
    clearAutoAdvanceTimeout();
    setFooterHint(null);
    if (isPreviewInspectMode) {
      return;
    }
    if (flowCompleted) {
      return;
    }
    if (currentPath.length <= 1) {
      return;
    }
    const nextPath = currentPath.slice(0, -1);
    setCurrentPath(nextPath);
    setCurrentNodeId(nextPath[nextPath.length - 1] || null);
  };

  const handlePrimaryButton = () => {
    clearAutoAdvanceTimeout();
    setFooterHint(null);
    if (isPreviewInspectMode) {
      return;
    }
    if (isSimpleGroupedMode) {
      if (hasSimpleRequiredMissing) {
        setFooterHint(T("review.flow_required_hint"));
        return;
      }
      handleSave();
      return;
    }
    if (flowCompleted) {
      handleSave();
      return;
    }
    if (!currentNode) return;

    if (currentNode.type === REVIEW_QUESTION_TYPES.RANKING_QUESTION) {
      const rankingAnswer = rankingQuestionRef.current?.confirmRanking() || [];
      const answered = Array.isArray(rankingAnswer) && rankingAnswer.length > 0;
      if (currentNode.required && !answered) {
        setFooterHint(T("review.flow_required_hint"));
        return;
      }
      const result = handleAnswerChange(currentNode.id, rankingAnswer, { autoAdvanceOverride: false });
      if (result?.updatedAnswers && result?.pathUpToCurrent) {
        continueAfterAnswer(currentNode.id, result.updatedAnswers, result.pathUpToCurrent, { delayMs: 0 });
      }
      return;
    }

    if (currentNode.type === REVIEW_QUESTION_TYPES.TEXTAREA_QUESTION) {
      const textareaAnswer =
        textareaQuestionRef.current?.confirmAnswer?.() ?? textareaLocalValue ?? answers[currentNode.id] ?? "";
      const answered = hasValue(textareaAnswer);
      if (currentNode.required && !answered) {
        setFooterHint(T("review.flow_required_hint"));
        return;
      }
      if (!currentNode.required && !answered) {
        advanceWithoutAnswer();
        return;
      }
      const result = handleAnswerChange(currentNode.id, textareaAnswer, { autoAdvanceOverride: false });
      if (result?.updatedAnswers && result?.pathUpToCurrent) {
        continueAfterAnswer(currentNode.id, result.updatedAnswers, result.pathUpToCurrent, { delayMs: 0 });
      }
      return;
    }

    const ans = answers[currentNode.id];
    const answered = hasValue(ans);
    if (currentNode.required && !answered) {
      setFooterHint(T("review.flow_required_hint"));
      return;
    }
    if (!currentNode.required && !answered) {
      advanceWithoutAnswer();
      return;
    }
    const idx = currentPath.indexOf(currentNode.id);
    const pathUpToCurrent =
      idx >= 0 ? currentPath.slice(0, idx + 1) : [...currentPath, currentNode.id];
    continueAfterAnswer(currentNode.id, answers, pathUpToCurrent, { delayMs: 0 });
  };

  const handleContactInfoChange = (e) => {
    const { name, value } = e.target;
    setContactInfo((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const currentNode = currentNodeId ? normalizedFlow.nodeById?.[currentNodeId] : null;
  const currentNodeAutoAdvanceConfig = resolveReviewFlowNodeAutoAdvanceConfig(currentNode);

  useEffect(() => {
    if (
      !currentNode ||
      currentNode.type !== REVIEW_QUESTION_TYPES.TEXTAREA_QUESTION ||
      !currentNodeAutoAdvanceConfig.enabled ||
      isSimpleGroupedMode ||
      isPreviewInspectMode
    ) {
      return undefined;
    }

    const nextValue = `${textareaLocalValue || ""}`;
    if (!nextValue.trim()) {
      lastTextareaAutoAdvanceKeyRef.current = "";
      clearAutoAdvanceTimeout();
      return undefined;
    }

    const autoAdvanceKey = `${currentNode.id}:${nextValue}`;
    if (lastTextareaAutoAdvanceKeyRef.current === autoAdvanceKey) {
      return undefined;
    }

    clearAutoAdvanceTimeout();
    autoAdvanceTimeoutRef.current = window.setTimeout(() => {
      lastTextareaAutoAdvanceKeyRef.current = autoAdvanceKey;
      const result = handleAnswerChange(currentNode.id, nextValue, { autoAdvanceOverride: false });
      if (result?.updatedAnswers && result?.pathUpToCurrent) {
        continueAfterAnswer(currentNode.id, result.updatedAnswers, result.pathUpToCurrent, { delayMs: 0 });
      }
    }, currentNodeAutoAdvanceConfig.delayMs);

    return () => clearAutoAdvanceTimeout();
  }, [
    clearAutoAdvanceTimeout,
    continueAfterAnswer,
    currentNode,
    currentNodeAutoAdvanceConfig.delayMs,
    currentNodeAutoAdvanceConfig.enabled,
    handleAnswerChange,
    isPreviewInspectMode,
    isSimpleGroupedMode,
    textareaLocalValue,
  ]);

  const flowCompleted = isSimpleGroupedMode || (!currentNodeId && !submittingFlowAnswers);
  const questionStepIndex = isSimpleGroupedMode ? totalQuestionCount + 1 : currentNodeId ? currentPath.length : totalQuestionCount + 1;
  const progressRatio = isSimpleGroupedMode ? 1 : Math.min(1, questionStepIndex / totalWizardSteps);

  const typeVisual = typeVisuals[currentNode?.type] || typeVisuals[REVIEW_QUESTION_TYPES.SINGLE_CHOICE_QUESTION];
  const isFullscreen = layout === "fullscreen";
  const isLandingPageNode = currentNode?.type === REVIEW_QUESTION_TYPES.LANDING_PAGE;
  const isMobileViewport = typeof window !== "undefined" ? window.innerWidth < 768 : false;
  const isLandingMobileLayout =
    isLandingPageNode && typeof window !== "undefined" ? window.innerWidth < 768 : false;
  const isOpinionScaleStandaloneLayout =
    currentNode?.type === REVIEW_QUESTION_TYPES.OPINION_SCALE_QUESTION && !isLandingPageNode && !isSimpleGroupedMode;
  const resolvedSimpleReviewConfig = (() => {
    if (!isSimpleGroupedMode) {
      return getSimpleReviewConfig(null);
    }
    if (simpleReviewConfig && typeof simpleReviewConfig === "object") {
      return {
        ...simpleReviewConfig,
        design: {
          ...SIMPLE_REVIEW_DESIGN_DEFAULTS,
          ...(simpleReviewConfig?.design || {}),
        },
      };
    }
    return getSimpleReviewConfig(null);
  })();
  const simpleReviewDesign = resolvedSimpleReviewConfig?.design || SIMPLE_REVIEW_DESIGN_DEFAULTS;
  const currentNodeTheme = resolveReviewFlowNodeTheme(normalizedFlow, currentNode);
  const landingImage = currentNode ? getReviewFlowImage(currentNode.image || "") : "";
  const questionNodeImage =
    currentNode && currentNode.type !== REVIEW_QUESTION_TYPES.LANDING_PAGE
      ? getReviewFlowImage(currentNode.image || "")
      : "";
  const isOpinionScaleDesktopSplitLayout =
    isOpinionScaleStandaloneLayout && !!questionNodeImage && !isMobileViewport;
  const isImmersiveQuestionLayout =
    !!questionNodeImage && !isLandingPageNode && !isSimpleGroupedMode && !isOpinionScaleStandaloneLayout;
  const isQuestionImageMobileLayout = isImmersiveQuestionLayout && isMobileViewport;
  const isPhotoQuestionMobileLayout =
    isMobileViewport && !!questionNodeImage && !isLandingPageNode && !isSimpleGroupedMode;
  const currentNodeMobileContentPosition = Object.values(REVIEW_FLOW_MOBILE_CONTENT_POSITIONS).includes(
    currentNode?.mobileContentPosition
  )
    ? currentNode.mobileContentPosition
    : REVIEW_FLOW_MOBILE_CONTENT_POSITIONS.CENTER;
  const currentNodeContentPositionJustifyContent = resolveReviewFlowContentPositionJustifyContent(
    currentNodeMobileContentPosition
  );
  const currentNodeSplitLayoutConfig = getReviewFlowLayoutConfig(currentNode, {
    hasImage: !!questionNodeImage,
    compact: isPhotoQuestionMobileLayout || isQuestionImageMobileLayout,
  });
  const currentNodeCardLayoutConfig = getReviewFlowLayoutConfig(currentNode, {
    hasImage: !!questionNodeImage,
    compact: isMobileViewport,
  });
  const shouldUseUnifiedDesktopQuestionLayout =
    !isMobileViewport && !isSimpleGroupedMode && !isLandingPageNode && !!currentNode;
  const resolvedSplitLayoutContentJustifyContent = shouldUseUnifiedDesktopQuestionLayout
    ? currentNodeContentPositionJustifyContent
    : currentNodeSplitLayoutConfig.contentJustifyContent;
  const resolvedSplitLayoutContentGap = shouldUseUnifiedDesktopQuestionLayout
    ? 24
    : currentNodeSplitLayoutConfig.contentGap;
  const resolvedSplitLayoutAnswerMarginTop = shouldUseUnifiedDesktopQuestionLayout
    ? 22
    : currentNodeSplitLayoutConfig.answerMarginTop;
  const resolvedSplitLayoutFooterMarginTop = shouldUseUnifiedDesktopQuestionLayout
    ? 24
    : currentNodeSplitLayoutConfig.footerMarginTop;
  const resolvedCardContainerJustifyContent = shouldUseUnifiedDesktopQuestionLayout
    ? currentNodeContentPositionJustifyContent
    : currentNodeCardLayoutConfig.cardContainerJustifyContent;
  const resolvedCardAnswerMarginTop = shouldUseUnifiedDesktopQuestionLayout
    ? 22
    : currentNodeCardLayoutConfig.cardAnswerMarginTop;
  const mobilePhotoQuestionImageHeight = currentNodeSplitLayoutConfig.mobileImageHeight;
  const showTopProgressBar = !isLandingPageNode && !isSimpleGroupedMode;
  const currentNodeButtonLabel =
    (currentNode &&
      i18nValue(currentNodeTheme, "buttonLabel", language, null, currentNodeTheme.buttonLabel)) ||
    T("general.continue");
  const landingDuration =
    (currentNode && i18nValue(currentNode, "estimatedDuration", language, null, currentNode.estimatedDuration)) || "";
  const landingBackgroundColor = currentNodeTheme.backgroundColor;
  const landingTextColor = currentNodeTheme.textColor;
  const landingButtonBackgroundColor = currentNodeTheme.buttonBackgroundColor;
  const landingButtonTextColor = currentNodeTheme.buttonTextColor;
  const currentNodeTextAlign =
    currentNode && ["left", "center", "right"].includes(currentNode.textAlign) ? currentNode.textAlign : "left";
  const currentNodeFontFamily = resolveReviewFlowFontFamily({ fontFamily: currentNodeTheme?.fontFamily });
  const currentNodeTitleFontSize = resolveReviewFlowFontSize(currentNodeTheme?.titleFontSize);
  const currentNodeDescriptionFontSize = resolveReviewFlowFontSize(currentNodeTheme?.descriptionFontSize);
  const currentNodeHeaderBlockMinHeight = resolveReviewFlowBlockHeight(currentNode?.headerBlockMinHeight);
  const currentNodeAnswerBlockMinHeight = resolveReviewFlowBlockHeight(currentNode?.answerBlockMinHeight);
  const questionBackgroundColor = isSimpleGroupedMode ? simpleReviewDesign.cardBackgroundColor : currentNodeTheme.backgroundColor;
  const questionTextColor = isSimpleGroupedMode ? simpleReviewDesign.textColor : currentNodeTheme.textColor;
  const questionButtonBackgroundColor = isSimpleGroupedMode
    ? simpleReviewDesign.accentColor
    : currentNodeTheme.buttonBackgroundColor;
  const questionButtonTextColor = isSimpleGroupedMode
    ? getReadableTextColor(simpleReviewDesign.accentColor)
    : currentNodeTheme.buttonTextColor;
  const questionBorderColor = isSimpleGroupedMode
    ? simpleReviewDesign.accentMutedColor
    : withHexOpacity(questionButtonBackgroundColor, "3D");
  const questionSoftBorderColor = isSimpleGroupedMode
    ? simpleReviewDesign.accentMutedColor
    : withHexOpacity(questionButtonBackgroundColor, "26");
  const questionSoftFillColor = isSimpleGroupedMode
    ? withHexOpacity(simpleReviewDesign.accentColor, "18")
    : withHexOpacity(questionButtonBackgroundColor, "18");
  const questionShadowColor = isSimpleGroupedMode
    ? withHexOpacity(simpleReviewDesign.accentColor, "10")
    : withHexOpacity(questionButtonBackgroundColor, "18");
  const questionFooterShadowColor = isSimpleGroupedMode
    ? withHexOpacity(simpleReviewDesign.accentColor, "10")
    : withHexOpacity(questionButtonBackgroundColor, "14");
  const questionInputBackgroundColor = isSimpleGroupedMode ? "#FFFFFF" : withHexOpacity(questionButtonBackgroundColor, "10");
  const simpleGroupedBorderColor = simpleReviewDesign.accentMutedColor;
  const simpleGroupedMutedTextColor = withHexOpacity(simpleReviewDesign.textColor, "99");
  const simpleGroupedEmptyStarColor = simpleReviewDesign.accentMutedColor;
  const simpleGroupedFilledStarColor = simpleReviewDesign.accentColor;
  const simpleGroupedFontFamily =
    SIMPLE_REVIEW_FONT_FAMILIES[simpleReviewDesign.fontFamily] || SIMPLE_REVIEW_FONT_FAMILIES.modern;
  const shouldUseMobileFixedFooter =
    isMobileViewport &&
    !loading &&
    !submittingFlowAnswers &&
    !isPreviewInspectMode &&
    !isSimpleGroupedMode &&
    !flowCompleted &&
    !!currentNode;
  const footerBackgroundColor = isLandingPageNode ? landingBackgroundColor : questionBackgroundColor;
  const footerButtonBackgroundColor = isLandingPageNode ? landingButtonBackgroundColor : questionButtonBackgroundColor;
  const footerButtonTextColor = isLandingPageNode ? landingButtonTextColor : questionButtonTextColor;
  const footerBorderColor = withHexOpacity(footerButtonBackgroundColor, "26");
  const footerShadowColor = withHexOpacity(footerButtonBackgroundColor, "14");
  const shouldUseStandardMobileQuestionLayout =
    isMobileViewport &&
    !loading &&
    !submittingFlowAnswers &&
    !!currentNode &&
    !isLandingPageNode &&
    !isSimpleGroupedMode;

  const shellStyle = isFullscreen
    ? {
        flex: 1,
        minHeight: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: isLandingPageNode ? landingBackgroundColor : questionBackgroundColor,
        paddingBottom: shouldUseMobileFixedFooter ? "92px" : 0,
        width: "100%",
        position: "relative",
        fontFamily: isSimpleGroupedMode ? simpleGroupedFontFamily : undefined,
      }
    : {
        display: "flex",
        flexDirection: "column",
        maxHeight: "min(70vh, 520px)",
        minHeight: 320,
        position: "relative",
        paddingBottom: shouldUseMobileFixedFooter ? "92px" : 0,
        fontFamily: isSimpleGroupedMode ? simpleGroupedFontFamily : undefined,
      };

  const renderQuestionInput = (nodeOverride = null) => {
    const activeNode = nodeOverride || currentNode;
    if (!activeNode) return null;
    const isSimpleInput = isSimpleGroupedMode;
    const activeNodeAutoAdvanceConfig = resolveReviewFlowNodeAutoAdvanceConfig(activeNode);

    if (activeNode.type === REVIEW_QUESTION_TYPES.RANKING_QUESTION) {
      return (
        <RankingQuestionInput
          ref={rankingQuestionRef}
          activeNode={activeNode}
          answers={answers}
          handleAnswerChange={handleAnswerChange}
          onAnswerChange={() => setRankingHasAnswer(true)}
          autoAdvanceEnabled={activeNodeAutoAdvanceConfig.enabled}
          language={language}
          i18nValue={i18nValue}
          colors={{
            questionButtonBackgroundColor,
            questionButtonTextColor,
            questionBorderColor,
            questionSoftBorderColor,
            questionTextColor,
            isSimpleInput,
            simpleGroupedBorderColor,
            questionInputBackgroundColor,
          }}
        />
      );
    }

    if (activeNode.type === REVIEW_QUESTION_TYPES.SINGLE_CHOICE_QUESTION) {
      return (
        <div className="d-flex flex-column" style={{ gap: 10 }}>
          {(activeNode.options || []).map((option) => {
            const optionLabel = i18nValue(option, "label", language, null, option.label);
            const isSelected = answers?.[activeNode.id] === option.id;
            return (
              <button
                key={option.id}
                type="button"
                className="d-flex align-items-center text-left border-0 w-100"
                style={{
                  gap: 10,
                  padding: "14px 16px",
                  borderRadius: 12,
                  border: `1px solid ${isSelected ? questionButtonBackgroundColor : isSimpleInput ? simpleGroupedBorderColor : questionSoftBorderColor}`,
                  background: isSelected ? questionButtonBackgroundColor : questionInputBackgroundColor,
                  fontSize: 15,
                  color: isSelected ? questionButtonTextColor : questionTextColor,
                  boxShadow: isSelected
                    ? `0 8px 20px ${withHexOpacity(questionButtonBackgroundColor, "22")}`
                    : "0 1px 3px rgba(15,23,42,0.06)",
                }}
                onClick={() => handleAnswerChange(activeNode.id, option.id)}
              >
                <span
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    border: `2px solid ${isSelected ? questionButtonBackgroundColor : isSimpleInput ? simpleGroupedBorderColor : questionBorderColor}`,
                    background: isSelected ? questionButtonBackgroundColor : "transparent",
                  }}
                >
                  {isSelected ? (
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: questionButtonTextColor,
                      }}
                    />
                  ) : null}
                </span>
                <span>{optionLabel}</span>
              </button>
            );
          })}
        </div>
      );
    }

    if (activeNode.type === REVIEW_QUESTION_TYPES.OPINION_SCALE_QUESTION) {
      const min = activeNode.scaleMin ?? 0;
      const max = activeNode.scaleMax ?? 10;
      const lowLabel = i18nValue(activeNode, "lowLabel", language, null, activeNode.lowLabel);
      const highLabel = i18nValue(activeNode, "highLabel", language, null, activeNode.highLabel);
      const scaleValues = Array.from({ length: Math.max(max - min + 1, 0) }, (_, index) => min + index);
      const firstRowValues = scaleValues.slice(0, 6);
      const secondRowValues = scaleValues.slice(6);
      const renderScaleButton = (scaleValue) => {
        const isSelected = Number(answers?.[activeNode.id]) === scaleValue;
        return (
          <button
            key={`${activeNode.id}-${scaleValue}`}
            type="button"
            className="d-flex align-items-center justify-content-center border-0"
            style={{
              width: "100%",
              minHeight: isPhotoQuestionMobileLayout ? 42 : isMobileViewport ? 48 : 50,
              borderRadius: isPhotoQuestionMobileLayout ? 8 : 0,
              border: `1px solid ${
                isSelected
                  ? questionButtonBackgroundColor
                  : isSimpleInput
                    ? simpleGroupedBorderColor
                    : questionSoftBorderColor
              }`,
              background: isSelected
                ? questionButtonBackgroundColor
                : isSimpleInput
                  ? "#FFFFFF"
                  : questionInputBackgroundColor,
              color: isSelected ? questionButtonTextColor : questionTextColor,
              fontWeight: isPhotoQuestionMobileLayout ? 500 : 400,
              fontSize: isMobileViewport ? 16 : 17,
              boxShadow: isSelected
                ? `0 8px 20px ${withHexOpacity(questionButtonBackgroundColor, "22")}`
                : "0 1px 3px rgba(15,23,42,0.06)",
            }}
            onClick={() => handleAnswerChange(activeNode.id, scaleValue)}
          >
            {scaleValue}
          </button>
        );
      };

      return (
        <div style={{ width: "100%", maxWidth: 560, margin: "0 auto" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
              gap: isMobileViewport ? 8 : 10,
              width: "100%",
              marginBottom: secondRowValues.length ? (isMobileViewport ? 10 : 12) : 0,
            }}
          >
            {firstRowValues.map(renderScaleButton)}
          </div>
          {secondRowValues.length ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                gap: isMobileViewport ? 8 : 10,
                width: "100%",
                maxWidth: "calc((100% - 40px) * 5 / 6)",
                margin: "0 auto",
              }}
            >
              {secondRowValues.map(renderScaleButton)}
            </div>
          ) : null}
          {lowLabel || highLabel ? (
            <div
              style={{
                marginTop: 18,
                color: withHexOpacity(questionTextColor, "D1"),
                fontSize: isMobileViewport ? 13 : 14,
                lineHeight: 1.6,
                textAlign: "left",
              }}
            >
              {lowLabel ? <div>{`${min} -> ${lowLabel}`}</div> : null}
              {highLabel ? <div>{`${max} -> ${highLabel}`}</div> : null}
            </div>
          ) : null}
        </div>
      );
    }

    if (activeNode.type === REVIEW_QUESTION_TYPES.TEXTAREA_QUESTION) {
      return (
        <TextareaQuestionInput
          ref={textareaQuestionRef}
          activeNode={activeNode}
          answers={answers}
          language={language}
          i18nValue={i18nValue}
          onValueChange={(value) => {
            lastTextareaAutoAdvanceKeyRef.current = "";
            setTextareaLocalValue(value);
          }}
          colors={{
            questionButtonBackgroundColor,
            questionButtonTextColor,
            questionBorderColor,
            questionSoftBorderColor,
            questionTextColor,
            isSimpleInput,
            simpleGroupedBorderColor,
            questionBackgroundColor,
          }}
          autoGrowSingleLine={!isSimpleInput}
        />
      );
    }

    const starCount = Math.max(Number(activeNode.scaleMax || 5), 1);
    const selected = Number(answers?.[activeNode.id]) || 0;
    const starColor = isSimpleInput ? simpleGroupedFilledStarColor : questionButtonBackgroundColor;
    const emptyStarColor = isSimpleInput
      ? simpleGroupedEmptyStarColor
      : withHexOpacity(questionButtonBackgroundColor, "33");

    return (
      <div
        className={`d-flex flex-column ${isSimpleInput ? "align-items-start" : "align-items-center"}`}
        style={{ gap: isSimpleInput ? 0 : 16, width: "100%" }}
      >
        <div className={`d-flex ${isSimpleInput ? "justify-content-start" : "justify-content-center"}`} style={{ gap: 8 }}>
          {Array.from({ length: starCount }).map((_, index) => {
            const value = index + 1;
            const active = value <= selected;
            return (
              <button
                key={value}
                type="button"
                className={`d-flex ${isSimpleInput ? "align-items-start" : "flex-column align-items-center"} border-0 bg-transparent p-0`}
                style={{ minWidth: isSimpleInput ? "auto" : 52 }}
                onClick={() => handleAnswerChange(activeNode.id, value)}
              >
                <span
                  style={{
                    fontSize: 36,
                    lineHeight: 1,
                    color: active ? starColor : emptyStarColor,
                    WebkitTextStroke: `2px ${active ? starColor : emptyStarColor}`,
                  }}
                >
                  ★
                </span>
                {!isSimpleInput ? (
                  <span style={{ fontSize: 14, fontWeight: 700, color: questionTextColor, marginTop: 4 }}>{value}</span>
                ) : null}
              </button>
            );
          })}
        </div>
        {!isSimpleInput ? (
          <p className="text-center mb-0 small" style={{ maxWidth: 320, color: withHexOpacity(questionTextColor, "B8") }}>
            {T("review.rating_scale_hint")}
          </p>
        ) : null}
      </div>
    );
  };

  const renderStandardMobileQuestionLayout = () => {
    if (!currentNode) {
      return null;
    }

    const mobileContentJustifyContent =
      currentNodeMobileContentPosition === REVIEW_FLOW_MOBILE_CONTENT_POSITIONS.TOP
        ? "flex-start"
        : currentNodeMobileContentPosition === REVIEW_FLOW_MOBILE_CONTENT_POSITIONS.BOTTOM
          ? "flex-end"
          : "center";

    return (
      <div
        style={{
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          background: questionBackgroundColor,
        }}
      >
        {questionNodeImage ? (
          <div
            style={{
              width: "100%",
              height: mobilePhotoQuestionImageHeight,
              background: `center / cover no-repeat url("${questionNodeImage}")`,
              flexShrink: 0,
            }}
          />
        ) : null}

        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: mobileContentJustifyContent,
            width: "100%",
            // padding: questionNodeImage ? "18px 0 0" : "0",
            color: questionTextColor,
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 520,
              margin: "0 auto",
              padding: "32px 16px 0",
              color: questionTextColor,
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 24,
                height: 24,
                padding: "0 6px",
                borderRadius: 6,
                background: "#FFFFFF",
                color: '#000000',
                fontSize: 13,
                fontWeight: 700,
                marginBottom: 18,
              }}
            >
              {currentPath.length}
            </div>

            <h1
              style={{
                fontSize: currentNodeTitleFontSize || 22,
                lineHeight: 1.18,
                fontWeight: 500,
                marginBottom: 12,
                color: questionTextColor,
                textAlign: currentNodeTextAlign,
                fontFamily: currentNodeFontFamily,
              }}
            >
              {i18nValue(currentNode, "title", language, null, currentNode.title)}
              {currentNode.required ? <span className="text-danger ml-1">*</span> : null}
            </h1>

            {i18nValue(currentNode, "description", language, null, currentNode.description) ? (
              <p
                style={{
                  fontSize: currentNodeDescriptionFontSize || 15,
                  lineHeight: 1.45,
                  color: withHexOpacity(questionTextColor, "D1"),
                  marginBottom: 22,
                  textAlign: currentNodeTextAlign,
                  fontFamily: currentNodeFontFamily,
                }}
              >
                {i18nValue(currentNode, "description", language, null, currentNode.description)}
              </p>
            ) : null}

            <div style={{ width: "100%" }}>{renderQuestionInput()}</div>
          </div>
        </div>
      </div>
    );
  };

  const renderFeedbackForm = () => (
    <div style={{ maxWidth: 560, margin: "0 auto", width: "100%" }}>
      <textarea
        className="w-100 mb-3"
        rows={5}
        style={{
          border: `2px solid ${focusedField === "comment" ? questionButtonBackgroundColor : questionBorderColor}`,
          borderRadius: 12,
          padding: 14,
          fontSize: 15,
          background: questionBackgroundColor,
          color: questionTextColor,
          outline: "none",
          boxShadow: "none",
        }}
        placeholder={T("review.rate-placeholder")}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        onFocus={() => setFocusedField("comment")}
        onBlur={() => setFocusedField((current) => (current === "comment" ? null : current))}
      />
      {!roomOccupationId ? (
        <div className="w-100 pt-3">
          <h6 className="mb-4" style={{ color: questionTextColor, fontSize: 16, fontWeight: 400, lineHeight: 1.45 }}>
            {T("review.contact")}
          </h6>
          <input
            type="text"
            name="name"
            placeholder={T("checkin.inputs.name")}
            value={contactInfo.name || ""}
            onChange={handleContactInfoChange}
            onFocus={() => setFocusedField("name")}
            onBlur={() => setFocusedField((current) => (current === "name" ? null : current))}
            className="w-100 mb-2"
            style={{
              border: "none",
              borderBottom: `2px solid ${focusedField === "name" ? questionButtonBackgroundColor : simpleGroupedBorderColor}`,
              borderRadius: 0,
              fontSize: 14,
              padding: "10px 0 8px",
              color: questionTextColor,
              background: "transparent",
              outline: "none",
              boxShadow: "none",
            }}
          />
          {errors.name && <small className="text-danger d-block mb-2">{errors.name}</small>}
          <input
            type="email"
            name="email"
            placeholder={T("checkin.e-mail")}
            value={contactInfo.email || ""}
            onChange={handleContactInfoChange}
            onFocus={() => setFocusedField("email")}
            onBlur={() => setFocusedField((current) => (current === "email" ? null : current))}
            className="w-100 mb-2"
            style={{
              border: "none",
              borderBottom: `2px solid ${focusedField === "email" ? questionButtonBackgroundColor : simpleGroupedBorderColor}`,
              borderRadius: 0,
              fontSize: 14,
              padding: "10px 0 8px",
              color: questionTextColor,
              background: "transparent",
              outline: "none",
              boxShadow: "none",
            }}
          />
          {errors.email && <small className="text-danger d-block mb-2">{errors.email}</small>}
          <input
            type="text"
            name="phone"
            placeholder={T("checkin.phone")}
            value={contactInfo.phone || ""}
            onChange={handleContactInfoChange}
            onFocus={() => setFocusedField("phone")}
            onBlur={() => setFocusedField((current) => (current === "phone" ? null : current))}
            className="w-100"
            style={{
              border: "none",
              borderBottom: `2px solid ${focusedField === "phone" ? questionButtonBackgroundColor : simpleGroupedBorderColor}`,
              borderRadius: 0,
              fontSize: 14,
              padding: "10px 0 8px",
              color: questionTextColor,
              background: "transparent",
              outline: "none",
              boxShadow: "none",
            }}
          />
          {errors.phone && <small className="text-danger d-block mt-2">{errors.phone}</small>}
        </div>
      ) : null}
    </div>
  );

  const primaryLabel = flowCompleted ? T("review.flow_submit") : T("review.flow_ok");
  const footerPrimaryLabel = flowCompleted ? primaryLabel : currentNodeButtonLabel;
  const canGoBack = !isSimpleGroupedMode && !flowCompleted && currentPath.length > 1;
  const hasSimpleRequiredMissing =
    isSimpleGroupedMode && simpleQuestions.some((node) => node.required && !hasValue(answers?.[node.id]));
  
  const primaryDisabled = (() => {
    if (submittingFlowAnswers || loading || hasSimpleRequiredMissing) {
      return true;
    }
    
    if (flowCompleted || !currentNode) {
      return false;
    }
    
    if (!currentNode.required) {
      return false;
    }
    
    // For ranking questions, check if there's an answer selected
    if (currentNode.type === REVIEW_QUESTION_TYPES.RANKING_QUESTION) {
      return !rankingHasAnswer;
    }
    
    // For textarea questions, check if there's content in the local state
    if (currentNode.type === REVIEW_QUESTION_TYPES.TEXTAREA_QUESTION) {
      return !textareaLocalValue || textareaLocalValue.trim().length === 0;
    }
    
    // For other required questions, check the answers map
    return !hasValue(answers[currentNode.id]);
  })();

  return (
    <div className="review-flow-form" style={shellStyle}>
      {/* Progress bar */}
      {showTopProgressBar ? (
        <div
          style={{
            position:
              isOpinionScaleStandaloneLayout || isPhotoQuestionMobileLayout
                ? "absolute"
                : isFullscreen
                  ? "sticky"
                  : "relative",
            top: 0,
            left: isOpinionScaleStandaloneLayout || isPhotoQuestionMobileLayout ? 0 : undefined,
            right: isOpinionScaleStandaloneLayout || isPhotoQuestionMobileLayout ? 0 : undefined,
            zIndex: isOpinionScaleStandaloneLayout || isPhotoQuestionMobileLayout ? 6 : 4,
            padding: isPhotoQuestionMobileLayout ? "12px 16px 8px" : isFullscreen ? "12px 20px 8px" : "8px 12px 4px",
            background:
              isPhotoQuestionMobileLayout || (isFullscreen && (isImmersiveQuestionLayout || isOpinionScaleStandaloneLayout))
                ? "transparent"
                : isFullscreen
                  ? questionBackgroundColor
                  : "transparent",
            pointerEvents: isOpinionScaleStandaloneLayout || isPhotoQuestionMobileLayout ? "none" : undefined,
          }}
        >
          <div
            style={{
              height: 6,
              borderRadius: 999,
              background: withHexOpacity(questionButtonBackgroundColor, "1F"),
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${Math.round(progressRatio * 100)}%`,
                borderRadius: 999,
                background: questionButtonBackgroundColor,
                transition: "width 0.35s ease",
              }}
            />
          </div>
        </div>
      ) : null}

      <div
        style={{
          flex: 1,
          overflow: isLandingPageNode || isImmersiveQuestionLayout ? "hidden" : "auto",
          padding: isLandingPageNode || isImmersiveQuestionLayout ? 0 : isFullscreen ? "0px" : "12px 16px 16px",
          background: isLandingPageNode ? landingBackgroundColor : questionBackgroundColor,
        }}
      >
        {loading && <ThanksReviews />}

        {submittingFlowAnswers && (
          <div className="text-center py-5 px-3">
            <div className="spinner-border mb-4" role="status" />
            <p className="mb-0 font-weight-bold">{T("review.sending_response")}</p>
          </div>
        )}

        {!loading && !submittingFlowAnswers && currentNode && isLandingPageNode ? (
          <div
            style={{
              minHeight: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              background: landingBackgroundColor,
            }}
          >
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: isLandingMobileLayout ? "column" : "row",
                flexWrap: "nowrap",
                minHeight: 0,
                height: "100%",
              }}
            >
              <div
                style={{
                  flex: isLandingMobileLayout ? "0 0 190px" : "1 1 52%",
                  minWidth: isLandingMobileLayout ? "100%" : 280,
                  minHeight: isLandingMobileLayout ? 220 : 300,
                  height: isLandingMobileLayout ? 190 : "100%",
                  background: landingImage ? `center / cover no-repeat url("${landingImage}")` : "#1f2937",
                }}
              />
              <div
                className="d-flex"
                style={{
                  flex: isLandingMobileLayout ? "1 1 auto" : "1 1 48%",
                  minWidth: isLandingMobileLayout ? "100%" : 280,
                  minHeight: 0,
                  height: isLandingMobileLayout ? "auto" : "100%",
                  padding: isLandingMobileLayout ? "28px 20px 22px" : isFullscreen ? "32px 24px 28px" : "24px 20px",
                  background: landingBackgroundColor,
                  color: landingTextColor,
                  alignItems: "stretch",
                  overflowY: "auto",
                }}
              >
                <div
                  className="d-flex flex-column justify-content-between w-100"
                  style={{
                    flex: 1,
                    minHeight: 0,
                    maxWidth: 420,
                    margin: "0 auto",
                    gap: 28,
                  }}
                >
                  <div style={{ minHeight: currentNodeHeaderBlockMinHeight || undefined }}>
                    <h1
                      style={{
                        fontSize: currentNodeTitleFontSize || (isLandingMobileLayout ? 24 : isFullscreen ? 40 : 28),
                        lineHeight: 1.15,
                        fontWeight: 500,
                        marginBottom: 24,
                        color: landingTextColor,
                        textAlign: currentNodeTextAlign,
                        fontFamily: currentNodeFontFamily,
                      }}
                    >
                      {i18nValue(currentNode, "title", language, null, currentNode.title)}
                    </h1>
                    {i18nValue(currentNode, "description", language, null, currentNode.description) ? (
                      <p
                        style={{
                          fontSize:
                            currentNodeDescriptionFontSize || (isLandingMobileLayout ? 15 : isFullscreen ? 18 : 16),
                          lineHeight: 1.45,
                          color: landingTextColor,
                          opacity: 0.92,
                          marginBottom: 0,
                          textAlign: currentNodeTextAlign,
                          fontFamily: currentNodeFontFamily,
                        }}
                      >
                        {i18nValue(currentNode, "description", language, null, currentNode.description)}
                      </p>
                    ) : null}
                  </div>

                  <div
                    style={{
                      marginTop: "auto",
                      minHeight: currentNodeAnswerBlockMinHeight || undefined,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-end",
                    }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{ minHeight: 36, marginBottom: 14, color: landingTextColor, opacity: 0.84, fontSize: 14 }}
                    >
                      {landingDuration ? (
                        <>
                          <FontAwesomeIcon icon={faClock} style={{ marginRight: 8 }} />
                          <span>{landingDuration}</span>
                        </>
                      ) : null}
                    </div>
                    {!shouldUseMobileFixedFooter ? (
                    <div className="d-flex align-items-center" style={{ gap: 12, marginTop: "auto" }}>
                      {canGoBack && !isPreviewInspectMode ? (
                        <button
                          type="button"
                          className="border-0 d-inline-flex align-items-center justify-content-center flex-shrink-0"
                          style={{
                            width: 52,
                            height: 52,
                            borderRadius: 0,
                            background: landingButtonBackgroundColor,
                            color: landingButtonTextColor,
                            opacity: 0.82,
                          }}
                          onClick={handleBackStep}
                          aria-label={T("general.back")}
                        >
                          <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="border-0 w-100 font-weight-bold"
                        style={{
                          minHeight: 52,
                          borderRadius: 0,
                          background: landingButtonBackgroundColor,
                          color: landingButtonTextColor,
                          fontSize: 16,
                          opacity: isPreviewInspectMode ? 0.7 : 1,
                        }}
                        disabled={loading || submittingFlowAnswers || isPreviewInspectMode}
                        onClick={handlePrimaryButton}
                      >
                        {currentNodeButtonLabel}
                      </button>
                    </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {shouldUseStandardMobileQuestionLayout ? renderStandardMobileQuestionLayout() : null}

        {!shouldUseStandardMobileQuestionLayout &&
        !loading &&
        !submittingFlowAnswers &&
        currentNode &&
        isOpinionScaleStandaloneLayout ? (
          <div
            style={{
              minHeight: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              background: questionBackgroundColor,
            }}
          >
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: isMobileViewport ? "column" : "row",
                flexWrap: "nowrap",
                minHeight: 0,
                height: "100%",
              }}
            >
              {questionNodeImage ? (
                <div
                  style={{
                    flex: isMobileViewport ? `0 0 ${mobilePhotoQuestionImageHeight}px` : "1 1 52%",
                    minWidth: isMobileViewport ? "100%" : 280,
                    minHeight: isMobileViewport ? mobilePhotoQuestionImageHeight : 300,
                    height: isMobileViewport ? mobilePhotoQuestionImageHeight : "100%",
                    background: `center ${isPhotoQuestionMobileLayout ? "center" : "center"} / cover no-repeat url("${questionNodeImage}")`,
                  }}
                />
              ) : null}
              <div
                className="d-flex"
                style={{
                  flex: isMobileViewport ? "1 1 auto" : "1 1 48%",
                  minWidth: isMobileViewport ? "100%" : 280,
                  minHeight: 0,
                  height: isMobileViewport ? "100%" : "100%",
                  padding: isMobileViewport
                    ? `28px 16px calc(18px + env(safe-area-inset-bottom, 0px))`
                    : isFullscreen
                      ? "32px 24px 28px"
                      : "24px 20px",
                  background: questionBackgroundColor,
                  color: questionTextColor,
                  alignItems: "stretch",
                  overflowY: "auto",
                }}
              >
                <div
                  className="d-flex flex-column w-100"
                  style={{
                    flex: 1,
                    minHeight: 0,
                    maxWidth: isPhotoQuestionMobileLayout ? 520 : 420,
                    margin: "0 auto",
                    gap: resolvedSplitLayoutContentGap,
                    justifyContent: resolvedSplitLayoutContentJustifyContent,
                  }}
                >
                  <div>
                    {!isPhotoQuestionMobileLayout ? (
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: 28,
                          height: 28,
                          padding: "0 8px",
                          borderRadius: 6,
                          background: questionButtonTextColor,
                          color: questionBackgroundColor,
                          fontSize: 14,
                          fontWeight: 700,
                          marginBottom: 22,
                        }}
                      >
                        {currentPath.length}
                      </div>
                    ) : null}
                    <h1
                      style={{
                        fontSize:
                          currentNodeTitleFontSize ||
                          (isPhotoQuestionMobileLayout ? 18 : isMobileViewport ? 24 : isFullscreen ? 40 : 28),
                        lineHeight: 1.15,
                        fontWeight: 500,
                        marginBottom: isPhotoQuestionMobileLayout ? 5 : 24,
                        color: questionTextColor,
                        textAlign: currentNodeTextAlign,
                        fontFamily: currentNodeFontFamily,
                      }}
                    >
                      {i18nValue(currentNode, "title", language, null, currentNode.title)}
                      {currentNode.required ? <span className="text-danger ml-1">*</span> : null}
                    </h1>
                    {i18nValue(currentNode, "description", language, null, currentNode.description) ? (
                      <p
                        style={{
                          fontSize: currentNodeDescriptionFontSize || (isMobileViewport ? 15 : isFullscreen ? 18 : 16),
                          lineHeight: 1.45,
                          color: questionTextColor,
                          opacity: 0.92,
                          marginBottom: 0,
                          textAlign: currentNodeTextAlign,
                          fontFamily: currentNodeFontFamily,
                        }}
                      >
                        {i18nValue(currentNode, "description", language, null, currentNode.description)}
                      </p>
                    ) : null}
                  </div>

                  <div
                    style={{
                      marginTop: resolvedSplitLayoutAnswerMarginTop,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div style={{ width: "100%", marginTop: isPhotoQuestionMobileLayout ? 4 : 0 }}>{renderQuestionInput()}</div>
                    {!isPreviewInspectMode && !shouldUseMobileFixedFooter ? (
                      <div style={{ marginTop: resolvedSplitLayoutFooterMarginTop }}>
                        {footerHint ? (
                          <small className="text-danger d-block mb-2" style={{ fontSize: 12 }}>
                            {footerHint}
                          </small>
                        ) : null}
                        <div className="d-flex align-items-center" style={{ gap: 12, marginTop: "auto" }}>
                          {canGoBack ? (
                            <button
                              type="button"
                              className="border-0 d-inline-flex align-items-center justify-content-center flex-shrink-0"
                              style={{
                                width: isPhotoQuestionMobileLayout ? 44 : 52,
                                height: isPhotoQuestionMobileLayout ? 44 : 52,
                                borderRadius: isPhotoQuestionMobileLayout ? 8 : 0,
                                background: questionButtonBackgroundColor,
                                color: questionButtonTextColor,
                                opacity: 0.82,
                              }}
                              onClick={handleBackStep}
                              aria-label={T("general.back")}
                            >
                              <FontAwesomeIcon icon={faChevronLeft} />
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="border-0 w-100 font-weight-bold"
                            style={{
                              minHeight: isPhotoQuestionMobileLayout ? 44 : 52,
                              borderRadius: isPhotoQuestionMobileLayout ? 8 : 0,
                              background: questionButtonBackgroundColor,
                              color: questionButtonTextColor,
                              fontSize: 16,
                              opacity: primaryDisabled ? 0.45 : 1,
                            }}
                            disabled={primaryDisabled}
                            onClick={handlePrimaryButton}
                          >
                            {primaryLabel}
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {!shouldUseStandardMobileQuestionLayout &&
        !loading &&
        !submittingFlowAnswers &&
        currentNode &&
        isImmersiveQuestionLayout ? (
          <div
            style={{
              minHeight: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              background: questionBackgroundColor,
            }}
          >
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: isQuestionImageMobileLayout ? "column" : "row",
                flexWrap: "nowrap",
                minHeight: 0,
                height: "100%",
              }}
            >
              <div
                style={{
                  flex: isQuestionImageMobileLayout ? `0 0 ${mobilePhotoQuestionImageHeight}px` : "1 1 52%",
                  minWidth: isQuestionImageMobileLayout ? "100%" : 280,
                  minHeight: isQuestionImageMobileLayout ? mobilePhotoQuestionImageHeight : 320,
                  height: isQuestionImageMobileLayout ? mobilePhotoQuestionImageHeight : "100%",
                  background: `center ${isQuestionImageMobileLayout ? "top" : "center"} / cover no-repeat url("${questionNodeImage}")`,
                }}
              />
              <div
                className="d-flex"
                style={{
                  flex: isQuestionImageMobileLayout ? "1 1 auto" : "1 1 48%",
                  minWidth: isQuestionImageMobileLayout ? "100%" : 280,
                  minHeight: 0,
                  height: isQuestionImageMobileLayout ? "100%" : "100%",
                  padding: isQuestionImageMobileLayout
                    ? `28px 16px calc(18px + env(safe-area-inset-bottom, 0px))`
                    : isFullscreen
                      ? "32px 24px 28px"
                      : "24px 20px",
                  background: questionBackgroundColor,
                  color: questionTextColor,
                  alignItems: "stretch",
                  overflowY: "auto",
                }}
              >
                <div
                  className="d-flex flex-column w-100"
                  style={{
                    flex: 1,
                    minHeight: 0,
                    maxWidth: isQuestionImageMobileLayout ? 520 : 440,
                    margin: "0 auto",
                    gap: resolvedSplitLayoutContentGap,
                    justifyContent: resolvedSplitLayoutContentJustifyContent,
                  }}
                >
                  <div>

                    <h1
                      style={{
                        fontSize: currentNodeTitleFontSize || (isQuestionImageMobileLayout ? 18 : isFullscreen ? 40 : 28),
                        lineHeight: 1.15,
                        fontWeight: 500,
                        marginBottom: isQuestionImageMobileLayout ? 5 : 18,
                        color: questionTextColor,
                        textAlign: currentNodeTextAlign,
                        fontFamily: currentNodeFontFamily,
                      }}
                    >
                      {i18nValue(currentNode, "title", language, null, currentNode.title)}
                      {currentNode.required ? <span className="text-danger ml-1">*</span> : null}
                    </h1>
                    {i18nValue(currentNode, "description", language, null, currentNode.description) ? (
                      <p
                        style={{
                          fontSize:
                            currentNodeDescriptionFontSize || (isQuestionImageMobileLayout ? 15 : isFullscreen ? 18 : 16),
                          lineHeight: 1.45,
                          color: questionTextColor,
                          opacity: 0.92,
                          marginBottom: 0,
                          textAlign: currentNodeTextAlign,
                          fontFamily: currentNodeFontFamily,
                        }}
                      >
                        {i18nValue(currentNode, "description", language, null, currentNode.description)}
                      </p>
                    ) : null}
                  </div>

                  <div
                    style={{
                      marginTop: resolvedSplitLayoutAnswerMarginTop,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div style={{ width: "100%", marginTop: isQuestionImageMobileLayout ? 4 : 0 }}>{renderQuestionInput()}</div>
                    {!isPreviewInspectMode && !shouldUseMobileFixedFooter ? (
                      <div style={{ marginTop: resolvedSplitLayoutFooterMarginTop }}>
                        {footerHint ? (
                          <small className="text-danger d-block mb-2" style={{ fontSize: 12 }}>
                            {footerHint}
                          </small>
                        ) : null}
                        <div className="d-flex align-items-center" style={{ gap: 12, marginTop: "auto" }}>
                          {canGoBack ? (
                            <button
                              type="button"
                              className="border-0 d-inline-flex align-items-center justify-content-center flex-shrink-0"
                              style={{
                                width: isQuestionImageMobileLayout ? 44 : 52,
                                height: isQuestionImageMobileLayout ? 44 : 52,
                                borderRadius: isQuestionImageMobileLayout ? 8 : 0,
                                background: questionButtonBackgroundColor,
                                color: questionButtonTextColor,
                                opacity: 0.82,
                              }}
                              onClick={handleBackStep}
                              aria-label={T("general.back")}
                            >
                              <FontAwesomeIcon icon={faChevronLeft} />
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="border-0 w-100 font-weight-bold"
                            style={{
                              minHeight: isQuestionImageMobileLayout ? 44 : 52,
                              borderRadius: isQuestionImageMobileLayout ? 8 : 0,
                              background: questionButtonBackgroundColor,
                              color: questionButtonTextColor,
                              fontSize: 16,
                              opacity: primaryDisabled ? 0.45 : 1,
                            }}
                            disabled={primaryDisabled}
                            onClick={handlePrimaryButton}
                          >
                            {primaryLabel}
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {!shouldUseStandardMobileQuestionLayout &&
        !loading &&
        !submittingFlowAnswers &&
        currentNode &&
        !isLandingPageNode &&
        !isSimpleGroupedMode &&
        !isOpinionScaleStandaloneLayout ? (
          !isImmersiveQuestionLayout ? (
          <div
            style={{
              width: "100%",
              minHeight: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: resolvedCardContainerJustifyContent,
            }}
          >
            <div style={{ width: "100%", maxWidth: 560, minWidth: 0, margin: "0 auto" }}>
              {questionNodeImage ? (
                <div
                  style={{
                    width: "100%",
                    minHeight: isMobileViewport ? mobilePhotoQuestionImageHeight : 180,
                    borderRadius: 18,
                    marginBottom: 16,
                    background: `center / cover no-repeat url("${questionNodeImage}")`,
                    boxShadow: `0 16px 40px ${questionShadowColor}`,
                  }}
                />
              ) : null}
              <div
                style={{
                  // border: `2px solid ${questionBorderColor}`,
                  borderRadius: 16,
                  padding: isFullscreen ? (isMobileViewport ? 18 : 22) : 16,
                  background: questionBackgroundColor,
                  color: questionTextColor,
                  width: "100%",
                  minWidth: 0,
                }}
              >
                <div
                  className="d-flex align-items-start"
                  style={{
                    gap: 10,
                    marginBottom: i18nValue(currentNode, "description", language, null, currentNode.description)
                      ? 6
                      : resolvedCardAnswerMarginTop,
                  }}
                >
                  <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0 }}>
                    <span
                      style={{
                        fontSize: currentNodeTitleFontSize || (isMobileViewport ? 12 : 13),
                        fontWeight: 700,
                        color: questionTextColor,
                        letterSpacing: "0.02em",
                        lineHeight: 1.35,
                        textAlign: currentNodeTextAlign,
                        display: "block",
                        fontFamily: currentNodeFontFamily,
                      }}
                    >
                      {i18nValue(currentNode, "title", language, null, currentNode.title)}
                      {currentNode.required ? <span className="text-danger ml-1">*</span> : null}
                    </span>
                  </div>
                </div>

                {i18nValue(currentNode, "description", language, null, currentNode.description) ? (
                  <p
                    style={{
                      fontSize: currentNodeDescriptionFontSize || 14,
                      lineHeight: 1.45,
                      color: withHexOpacity(questionTextColor, "C7"),
                      textAlign: currentNodeTextAlign,
                      fontFamily: currentNodeFontFamily,
                      marginBottom: resolvedCardAnswerMarginTop,
                    }}
                  >
                    {i18nValue(currentNode, "description", language, null, currentNode.description)}
                  </p>
                ) : null}

                <div>{renderQuestionInput()}</div>
                {!isPreviewInspectMode ? (
                  <div style={{ marginTop: resolvedSplitLayoutFooterMarginTop }}>
                    {footerHint ? (
                      <small className="text-danger d-block mb-2" style={{ fontSize: 12 }}>
                        {footerHint}
                      </small>
                    ) : null}
                    <div className="d-flex align-items-center" style={{ gap: 12, marginTop: "auto" }}>
                      {canGoBack ? (
                        <button
                          type="button"
                          className="border-0 d-inline-flex align-items-center justify-content-center flex-shrink-0"
                          style={{
                            width: 52,
                            height: 52,
                            borderRadius: 0,
                            background: questionButtonBackgroundColor,
                            color: questionButtonTextColor,
                            opacity: 0.82,
                          }}
                          onClick={handleBackStep}
                          aria-label={T("general.back")}
                        >
                          <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="border-0 w-100 font-weight-bold"
                        style={{
                          minHeight: 52,
                          borderRadius: 0,
                          background: questionButtonBackgroundColor,
                          color: questionButtonTextColor,
                          fontSize: 16,
                          opacity: primaryDisabled ? 0.45 : 1,
                        }}
                        disabled={primaryDisabled}
                        onClick={handlePrimaryButton}
                      >
                        {primaryLabel}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          ) : null
        ) : null}

        {!loading && !submittingFlowAnswers && isSimpleGroupedMode ? (
          <div style={{ maxWidth: 560, margin: "0 auto", width: "100%", display: "flex", flexDirection: "column", gap: 18 }}>
            <div
              style={{
                border: `2px solid ${simpleGroupedBorderColor}`,
                borderRadius: 16,
                padding: isFullscreen ? (isMobileViewport ? 18 : 22) : 16,
                background: questionBackgroundColor,
                color: questionTextColor,
                width: "100%",
              }}
            >
              {(simpleQuestions || []).map((node, index) => {
                const groupedNodeLayoutConfig = getReviewFlowLayoutConfig(node, { compact: true });

                return (
                  <div
                    key={node.id}
                    style={{
                      paddingBottom: index < simpleQuestions.length - 1 ? 18 : 0,
                      marginBottom: index < simpleQuestions.length - 1 ? 18 : 0,
                      borderBottom: index < simpleQuestions.length - 1 ? `1px solid ${simpleGroupedBorderColor}` : "none",
                    }}
                  >
                  <div
                    style={{
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        fontSize: resolveReviewFlowFontSize(node?.titleFontSize) || 20,
                        fontWeight: 700,
                        color: questionTextColor,
                        lineHeight: 1.35,
                        marginBottom: 6,
                        textAlign: ["left", "center", "right"].includes(node?.textAlign) ? node.textAlign : "left",
                        fontFamily: resolveReviewFlowFontFamily(node),
                      }}
                    >
                      {i18nValue(node, "title", language, null, node.title)}
                      {node.required ? <span className="text-danger ml-1">*</span> : null}
                    </div>
                    {i18nValue(node, "description", language, null, node.description) ? (
                      <p
                        style={{
                          fontSize: resolveReviewFlowFontSize(node?.descriptionFontSize) || 14,
                          lineHeight: 1.45,
                          color: simpleGroupedMutedTextColor,
                          textAlign: ["left", "center", "right"].includes(node?.textAlign) ? node.textAlign : "left",
                          fontFamily: resolveReviewFlowFontFamily(node),
                          marginBottom: groupedNodeLayoutConfig.groupedAnswerMarginTop,
                        }}
                      >
                        {i18nValue(node, "description", language, null, node.description)}
                      </p>
                    ) : null}
                  </div>
                  <div
                    style={{
                      marginTop: i18nValue(node, "description", language, null, node.description)
                        ? 0
                        : groupedNodeLayoutConfig.groupedAnswerMarginTop,
                    }}
                  >
                    {renderQuestionInput(node)}
                  </div>
                </div>
                );
              })}
            </div>
            {renderFeedbackForm()}
          </div>
        ) : null}

        {!loading && !isSimpleGroupedMode && flowCompleted && renderFeedbackForm()}
      </div>

      {/* Fixed footer */}
      {!loading &&
      !submittingFlowAnswers &&
      !isPreviewInspectMode &&
      !isMobileViewport &&
      flowCompleted &&
      !isLandingPageNode &&
      !isImmersiveQuestionLayout &&
      !isOpinionScaleStandaloneLayout && (
        <div
          style={{
            position: isFullscreen ? "fixed" : "sticky",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "14px 20px calc(14px + env(safe-area-inset-bottom, 0px))",
            background: questionBackgroundColor,
            borderTop: `1px solid ${questionSoftBorderColor}`,
            boxShadow: `0 -8px 24px ${questionFooterShadowColor}`,
          }}
        >
          <div
            className="d-flex align-items-center"
            style={{
              width: "100%",
              maxWidth: isOpinionScaleStandaloneLayout ? 720 : undefined,
              margin: isOpinionScaleStandaloneLayout ? "0 auto" : undefined,
              gap: 12,
            }}
          >
            <button
              type="button"
              className="border-0 d-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: canGoBack ? questionButtonBackgroundColor : "#cbd5e1",
                color: canGoBack ? questionButtonTextColor : "#fff",
                opacity: canGoBack ? 1 : 0.5,
              }}
              disabled={!canGoBack}
              onClick={handleBackStep}
              aria-label={T("general.back")}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            <div className="flex-grow-1 d-flex flex-column" style={{ minWidth: 0 }}>
              {footerHint ? (
                <small className="text-danger mb-1" style={{ fontSize: 12 }}>
                  {footerHint}
                </small>
              ) : null}
              <button
                type="button"
                className="border-0 w-100 font-weight-bold text-uppercase"
                style={{
                  minHeight: 48,
                  borderRadius: 12,
                  background: questionButtonBackgroundColor,
                  color: questionButtonTextColor,
                  fontSize: 15,
                  letterSpacing: "0.06em",
                  opacity: primaryDisabled ? 0.45 : 1,
                }}
                disabled={primaryDisabled}
                onClick={handlePrimaryButton}
              >
                {primaryLabel}
              </button>
            </div>

            {layout === "embedded" && onDismiss ? (
              <button
                type="button"
                className="btn btn-link btn-sm"
                style={{ color: withHexOpacity(questionTextColor, "C7") }}
                onClick={onDismiss}
              >
                {T("general.cancel")}
              </button>
            ) : null}
          </div>
        </div>
      )}

      {shouldUseMobileFixedFooter ? (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 12,
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 16px calc(12px + env(safe-area-inset-bottom, 0px))",
            background: footerBackgroundColor,
            borderTop: `1px solid ${footerBorderColor}`,
            boxShadow: `0 -8px 24px ${footerShadowColor}`,
          }}
        >
          <div className="d-flex align-items-center w-100" style={{ gap: 12 }}>
            <button
              type="button"
              className="border-0 d-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: 42,
                height: 42,
                borderRadius: 8,
                background: canGoBack ? footerButtonBackgroundColor : "#cbd5e1",
                color: canGoBack ? footerButtonTextColor : "#fff",
                opacity: canGoBack ? 1 : 0.5,
              }}
              disabled={!canGoBack}
              onClick={handleBackStep}
              aria-label={T("general.back")}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            <div className="flex-grow-1 d-flex flex-column" style={{ minWidth: 0 }}>
              {footerHint ? (
                <small className="text-danger mb-1" style={{ fontSize: 12 }}>
                  {footerHint}
                </small>
              ) : null}
              <button
                type="button"
                className="border-0 w-100 font-weight-bold text-uppercase"
                style={{
                  minHeight: 42,
                  borderRadius: 8,
                  background: footerButtonBackgroundColor,
                  color: footerButtonTextColor,
                  fontSize: 15,
                  letterSpacing: "0.04em",
                  opacity: primaryDisabled ? 0.45 : 1,
                }}
                disabled={primaryDisabled}
                onClick={handlePrimaryButton}
              >
                {footerPrimaryLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ReviewFlowForm;
